<template>
    <template v-if="quickSearch">
        <button class="btn-s-icon btn-6" :class="hasMediumScreen ? 'primary-action' : ''" @click="updateQuickSearch(null)">
            <div class="icon-reset-pos"></div>
            <span v-if="hasMediumScreen">{{$t('common.reset_1')}}</span>
        </button>
        <button v-if="!hasMediumScreen" class="btn-1 btn-s-icon primary-action" @click="togglePanel()">
            <div class="icon-close-pos"></div>
            <span>{{$t(`views.companies.toggle.${showPanel ? 'close' : 'open'}`)}}</span>
        </button>
    </template>

    <template v-else>
        <template v-if="mode === 'prospects'">
            <button class="btn-s-icon" :class="hasDefaultProspectsOptions ? 'btn-disabled' : 'btn-6'" v-tooltip="'tooltips.reset_filters'" :disabled="hasDefaultProspectsOptions" @click="resetOptions('prospects')">
                <div class="icon-reset-pos"></div>
            </button>
            <button v-if="hasMediumScreen" class="btn-s-icon" :class="loading || !companies?.length ? 'btn-disabled' : 'btn-2'" v-tooltip="'tooltips.download_prospects'" :disabled="loading || !companies?.length" @click="confirmDownloadPopup">
                <div class="icon-download-neg"></div>
            </button>
        </template>

        <template v-else-if="mode === 'all'">
            <button class="btn-s-icon" :class="hasDefaultOptions ? 'btn-disabled' : 'btn-6'" v-tooltip="'tooltips.reset_filters'" :disabled="hasDefaultOptions" @click="resetOptions()">
                <div class="icon-reset-pos"></div>
            </button>
            <button v-if="!audienceId " class="btn-s-icon" :class="loading || !qid ? 'btn-disabled' : 'btn-7'" v-tooltip="'tooltips.audience_create'" :disabled="loading || !qid" @click="createAudiencePopup">
                <div class="icon-save-neg"></div>
            </button>
            <button v-else-if="audience.editable" class="btn-s-icon" :class="loading ? 'btn-disabled' : 'btn-7'" v-tooltip="'tooltips.audience_update'" :disabled="loading" @click="editAudiencePopup">
                <div class="icon-save-neg"></div>
            </button>
            <button v-else-if="!audience.editable " class="btn-s-icon" :class="loading || !qid ? 'btn-disabled' : 'btn-7'" v-tooltip="'tooltips.audience_create'" :disabled="loading || !qid" @click="createAudiencePopup">
                <div class="icon-save-neg"></div>
            </button>
        </template>
        
        <button v-if="hasMediumScreen && mode === 'prospects'" class="btn-1 btn-s-icon primary-action" @click="openCompany(companies[0])" :disabled="loading || !companies?.length">
            <span v-if="!loading">{{$t('common.view_1')}}{{hasLargeScreen ? ' ' + $t('common.start_1_by_1') : ''}}</span>
            <div v-if="!loading" class="icon-arrow-pos"></div>
            <div v-else class="loading-spinner-type-1 primary"></div>
        </button>
        <button v-else-if="hasMediumScreen" class="btn-1 btn-s-icon primary-action" @click="routeToMode('new')" :disabled="loading || !companies?.length">
            <span v-if="!loading">{{hasLargeScreen ? $t('common.start_suspects') : $t('common.start_1') }}</span>
            <div v-if="!loading" class="icon-arrow-pos"></div>
            <div v-else class="loading-spinner-type-1 primary"></div>
        </button>
        <button v-else class="btn-1 btn-s-icon primary-action" @click="togglePanel()">
            <div class="icon-close-pos"></div>
            <span>{{$t(`views.companies.toggle.${showPanel ? 'close' : 'open'}`)}}</span>
        </button>
    </template>
</template>

<script>
    import { markRaw } from 'vue';
    import { mapState, mapActions } from 'pinia';
    import { useCompaniesStore } from '@/stores/companies';
    import { usePopupStore } from '@/stores/utils/popup';
    import { useScreenWidthStore } from '@/stores/utils/screenWidth';
    import { useAudienceStore } from '@/stores/audience';
    import AudienceCreateForm from '@/components/forms/AudienceCreateForm';
    import AudienceEditForm from '@/components/forms/AudienceEditForm';

    export default {
        name: "CompaniesActions",
        computed: {
            ...mapState(useScreenWidthStore, ['hasMediumScreen', 'hasLargeScreen']),
            ...mapState(useAudienceStore, ['audience']),
            ...mapState(useCompaniesStore, ['audienceId', 'mode', 'downloadLinks', 'showPanel', 'quickSearch', 'companies', 'qid', 'loading', 'hasDefaultOptions', 'hasDefaultProspectsOptions']),
        },
        methods: {
            ...mapActions(useCompaniesStore, ['togglePanel', 'resetOptions', 'updateQuickSearch', 'openCompany', 'downloadCompanies', 'routeToMode']),
            async fetchDownloadUrls() {
                if(!this.loading) {
                    const downloadReady = await this.downloadCompanies();
                    if(downloadReady) {
                        return true;
                    }
                    return false;
                }
            },
            startDownload() {
                if(this.downloadLinks && Object.keys(this.downloadLinks).length > 0) {
                    const url = this.downloadLinks.export_url_zip || this.downloadLinks.export_url_csv || this.downloadLinks.export_url_xlsx;
                    window.open(url, "_blank"); 
                }
            },
            editAudiencePopup() {
                usePopupStore().showPopup('form', {
                    title: 'popups.edit_audience.title',
                    description: 'popups.edit_audience.description',
                    formComponent: markRaw(AudienceEditForm),
                    onFormSuccess: () => {
                        // Updating is handled by watch->audience in CompaniesManager.
                    }
                });
            },
            createAudiencePopup() {
                usePopupStore().showPopup('form', {
                    title: 'popups.create_audience.title',
                    description: 'popups.create_audience.description',
                    formComponent: markRaw(AudienceCreateForm),
                    onFormSuccess: () => {
                        // Routing is handled by watch->audience in CompaniesManager.
                    }
                });
            },
            confirmDownloadPopup() {
                usePopupStore().showPopup('fetch', {
                    title: 'popups.confirm_download.title',
                    description: 'popups.confirm_download.description',
                    btnConfirm: "popups.confirm_download.button_confirm",
                    btnCancel: "common.cancel_1",
                    fetchFunction: this.fetchDownloadUrls,
                    onFetchSuccess: this.startDownloadPopup
                });
            },
            startDownloadPopup() {
                usePopupStore().showPopup('confirm', {
                    title: 'popups.start_download.title',
                    description: 'popups.start_download.description',
                    btnConfirm: "popups.start_download.button_confirm",
                    btnCancel: "common.cancel_1",
                    confirmFunction: this.startDownload,
                });
            }
        }
    }
</script>